import { WebAuth } from "auth0-js";
import Auth0Client from "@auth0/auth0-spa-js/dist/typings/Auth0Client";
import createAuth0Client from "@auth0/auth0-spa-js";
import store from "@/store";

const sleep = (time: number) => new Promise(r => setTimeout(r, time));

export class AuthClient {
  domain: string = process.env.VUE_APP_AUTH0_DOMAIN;
  clientId: string = process.env.VUE_APP_AUTH0_CLIENT_ID;
  defaultRedirectUrl: string =
    process.env.VUE_APP_AUTH0_AUDIENCE || process.env.VUE_APP_URL;
  audience?: string = process.env.VUE_APP_AUTH0_AUDIENCE;
  connection: string = process.env.VUE_APP_AUTH0_CONNECTION;

  isLoading: boolean = false;
  redirectHandled: boolean = false;
  isAuthenticated: boolean = false;

  get webClient(): WebAuth {
    return new WebAuth({
      clientID: this.clientId,
      domain: this.domain,
      audience: this.audience
    });
  }

  private _spaClient?: Auth0Client;
  private async getTempSpaClient(): Promise<Auth0Client> {
    while (!this._spaClient) {
      await sleep(50);
    }

    return this._spaClient;
  }

  private async getSpaClient(): Promise<Auth0Client> {
    const client = await this.getTempSpaClient();

    while (!this.redirectHandled) {
      await sleep(50);
    }

    return client;
  }

  // private _managementClient?: Management;
  // private get managementClient(): Management {
  //   if (!this._managementClient) {
  //     throw new Error("AUTH: Management Client not ready.");
  //   }
  //   return this._managementClient;
  // }
  //
  // private await get managementClient(): Management {
  //   // Create the management client
  //   return new Management({
  //     domain: this.domain,
  //     token: await this.getJWT(),
  //     clientID: this.clientId
  //   } as ManagementOptions);
  // }

  constructor() {
    this.isLoading = true;

    // Create the SPA client
    createAuth0Client({
      domain: this.domain,
      client_id: this.clientId,
      audience: this.audience,
      redirect_uri: this.defaultRedirectUrl
    })
      .then((client: Auth0Client) => {
        this._spaClient = client;

        // Check if there is a redirect and handle it.
        this.handleRedirect().then(() => {
          // Set data
          client.isAuthenticated().then((isAuthenticated: boolean) => {
            if (isAuthenticated) {
              this.update();
            }
          });
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  async update() {
    try {
      await this.getUser();
      await this.getJWT();
      this.isAuthenticated = await this.getIsAuthenticated();
    } catch (e) {
      this.isAuthenticated = false;
    }
  }

  async handleRedirect() {
    if (
      window.location.search.includes("code=") &&
      window.location.search.includes("state=")
    ) {
      console.debug("[AUTH] - [REDIRECT] - Detected...");
      const client = await this.getTempSpaClient();
      await client.handleRedirectCallback();
      this.redirectHandled = true;
      console.debug("[AUTH] - [REDIRECT] - Handled.");
    }
    this.redirectHandled = true;
  }

  async login(redirectUrl?: string) {
    const client = await this.getSpaClient();
    await client.loginWithRedirect({
      redirect_uri: redirectUrl || this.defaultRedirectUrl
    });
  }

  async getIsAuthenticated(): Promise<boolean> {
    const client = await this.getSpaClient();

    let isLoggedIn = await client.isAuthenticated();
    if (!isLoggedIn) {
      isLoggedIn = !!localStorage.jwt;
    }
    return isLoggedIn;
  }

  async getUser(): Promise<any> {
    const client = await this.getSpaClient();
    const user = await client.getUser();
    store.commit("setAuth0User", user);
    return user;
  }

  async getJWT(): Promise<string> {
    const client = await this.getSpaClient();
    const jwt = (await client.getIdTokenClaims()).__raw;
    store.commit("basic", {
      key: "jwt",
      value: jwt
    });
    localStorage.jwt = jwt;
    return jwt;
  }

  async logout() {
    const client: Auth0Client = await this.getSpaClient();
    this.isAuthenticated = false;
    store.commit("logout");
    client.logout({ returnTo: process.env.VUE_APP_URL });
  }

  changePassword(email: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.webClient.changePassword(
        {
          email: email,
          connection: this.connection
        },
        (error: any, response: any) => {
          if (error) {
            resolve(false);
          } else {
            resolve(true);
          }
        }
      );
    });
  }
}

export const authClient = new AuthClient();
